import type { Point, Stroke } from '../types/app.js';
import { isLineOrArrowTool } from '../shared/stroke-tool.js';

export { isLineOrArrowTool } from '../shared/stroke-tool.js';

const DEFAULT_CURVE_STEPS = 20;

function unwrapLongitude(value: number, reference: number): number {
  let unwrapped = value;
  while (unwrapped - reference > 180) unwrapped -= 360;
  while (unwrapped - reference < -180) unwrapped += 360;
  return unwrapped;
}

function wrapLongitude(value: number): number {
  let wrapped = value;
  while (wrapped > 180) wrapped -= 360;
  while (wrapped < -180) wrapped += 360;
  return wrapped;
}

export function lineCurveControl(stroke: Pick<Stroke, 'curveControls'>, segmentIndex: number): Point | null {
  return stroke.curveControls?.[segmentIndex] ?? null;
}

export function linePathPoints(
  stroke: Pick<Stroke, 'tool' | 'points' | 'curveControls'>,
  steps = DEFAULT_CURVE_STEPS,
): Point[] {
  if (!isLineOrArrowTool(stroke.tool) || stroke.points.length < 2 || !stroke.curveControls?.some(Boolean)) {
    return stroke.points;
  }
  const output: Point[] = [{ ...stroke.points[0] }];
  for (let segmentIndex = 0; segmentIndex < stroke.points.length - 1; segmentIndex += 1) {
    const start = stroke.points[segmentIndex];
    const end = stroke.points[segmentIndex + 1];
    const control = lineCurveControl(stroke, segmentIndex);
    if (!control) {
      output.push({ ...end });
      continue;
    }
    const controlLng = unwrapLongitude(control.lng, start.lng);
    const endLng = unwrapLongitude(end.lng, start.lng);
    for (let step = 1; step <= steps; step += 1) {
      const t = step / steps;
      const inverse = 1 - t;
      output.push({
        lat: inverse * inverse * start.lat + 2 * inverse * t * control.lat + t * t * end.lat,
        lng: wrapLongitude(inverse * inverse * start.lng + 2 * inverse * t * controlLng + t * t * endLng),
      });
    }
  }
  return output;
}

export function normalizedLineCurveControls(value: Array<Point | null> | undefined, pointCount: number): Array<Point | null> | undefined {
  if (!Array.isArray(value) || pointCount < 2) return undefined;
  const controls = Array.from({ length: pointCount - 1 }, (_, index) => {
    const control = value[index];
    if (!control) return null;
    return Number.isFinite(control.lat) && Number.isFinite(control.lng) && Math.abs(control.lat) <= 90
      ? { lat: control.lat, lng: control.lng }
      : null;
  });
  return controls.some(Boolean) ? controls : undefined;
}
